import { Component, ReactNode, createRef } from "react";
import { BaseMetaColumn, BaseMetaTable, FilterItem, FilterItemColumnType, NamingConvention, ObjectService, SearchOperation, SortItem } from "namirasoft-core";
import { NSLine, NSSpace, NSSpaceSizeType, NSTable, NSFilterBox, NSFilterBoxProps_Table, NSFilterBoxProps_Column, TableColumnInfo, TableRowInfo, IBaseComponentProps, NSFilterBoxItems, NSRow, NSButton, NSBoxCombo, NSDialog, NSBoxBooleans, NSPanel, NSTitle, NSBox, NSColumn, NSBoxBoolean, NSBoxBaseCombo } from "namirasoft-site-react";
import { CategoryRow, FieldRow } from "namirasoft-field";
import { IEntityInfo } from "../IEntityInfo";
import { NSARouterMakerProps } from "../NSARouterMakerProps";
import { NSASectionViewTabPage, NSASectionViewTabPageUIProps } from "./NSASectionViewTabPage";
import { FilterItemColumnTypeBuilder, VariableType } from "namirasoft-schema";
import Styles from "./NSASectionList.module.css";

export interface NSASectionListCustomProps<RowType extends { id: string }, RowTypeInput = RowType> 
{
	ui?: {
		getRowAttributes?: (row_value: RowType, row_index: number, res: { rows: RowType[]; count: number; } | null) => { [key: string]: any };
		getColumnAttributes?: (column: TableColumnInfo) => { [key: string]: any };
		getCellFormattedValue?: (value: any, column: TableColumnInfo, row: TableRowInfo<RowType>) => any;
		modes?: { [name: string]: (entity: IEntityInfo<RowType, RowTypeInput>) => JSX.Element }
		default_mode?: string;
		search_box?: {
			exclude?: {
				show?: {
					table?: (table: string) => boolean;
					column?: (table: string, column: string) => boolean;
				},
				load_value?: (table: string, column: string) => boolean;
			}
		};
		tab_page_view?: NSASectionViewTabPageUIProps;
	};
	onIDsChanged?: () => void;
}

export interface NSASectionListProps<RowType extends { id: string }, RowTypeInput = RowType> extends IBaseComponentProps, NSARouterMakerProps, NSASectionListCustomProps<RowType, RowTypeInput>
{
	// model: {
	// 	id: string | null;
	// 	row: RowType | null;
	// };
	entity: IEntityInfo<RowType, RowTypeInput>;
	ctf: {
		enabled: boolean;
		fields: FieldRow[] | null;
		categories: CategoryRow[] | null;
	};
	deleteFieldCache: () => void;
	children?: ReactNode;
}

export interface NSASectionListState<RowType>
{
	res: {
		rows: RowType[];
		count: number;
	} | null;
	show_dialog_columns: boolean;
	sorts: SortItem[];
	mode: string;
}

export class NSASectionList<RowType extends { id: string }, RowTypeInput = RowType>
	extends Component<NSASectionListProps<RowType, RowTypeInput>, NSASectionListState<RowType>>
{
	public NSFilterBox_Top = createRef<NSFilterBox>();
	public NSBoxCombo_Mode = createRef<NSBoxCombo>();
	public NSTable_Main = createRef<NSTable<RowType>>();
	public NSBoxBoolean_Sort = createRef<NSBoxBoolean>();
	public NSBoxBaseCombo_Sort_Column = createRef<NSBoxBaseCombo<TableColumnInfo>>();
	public NSBoxCombo_Sort_Direction = createRef<NSBoxCombo>();
	public NSBoxBooleans_Columns = createRef<NSBoxBooleans>();
	public NSASectionViewTabPage_Ref = createRef<NSASectionViewTabPage<RowType, RowTypeInput>>();

	constructor(props: NSASectionListProps<RowType, RowTypeInput>)
	{
		super(props);
		this.state = { res: null, show_dialog_columns: false, sorts: [], mode: props.ui?.default_mode ?? "" };
	}

	getSelectedIDs(): string[]
	{
		return this.NSTable_Main.current?.getSelectedIDs() ?? [];
	}
	getSelectedRows(): RowType[]
	{
		return this.NSTable_Main.current?.getSelectedRows() ?? [];
	}

	override render()
	{
		const getColumns = (): TableColumnInfo[] =>
		{
			let cs = this.props.entity.client.getColumns();
			cs.forEach(c =>
			{
				c.text = NamingConvention.auto.convert(c.text, NamingConvention.Pascal_Case_Space);
			})
			return cs;
		};
		const getRowKey = (row: TableRowInfo<RowType>): string => 
		{
			return row.value.id.toString();
		};
		let ids = this.getSelectedIDs();
		let id = "";
		if (ids.length === 1)
			id = ids[0];

		let filterItems = this.NSFilterBox_Top.current?.getFilterItems() ?? [];
		let incommingFilterItems: FilterItem[] | undefined = undefined;
		try
		{
			let encoded = this.props.url.getQuery("filters");
			if (encoded)
				incommingFilterItems = FilterItem.decode(encoded) ?? undefined;
		} catch (error)
		{
		}

		function typeConvertor(type: string): FilterItemColumnType
		{
			let t = new ObjectService(type).getEnum(VariableType, VariableType.Any)
			let builder = new FilterItemColumnTypeBuilder(t);
			return builder.runSynch(() => { }) ?? FilterItemColumnType.Unknown;
		}

		// Modes
		let table_renderer = () =>
		{
			return <NSTable<RowType>
				ref={this.NSTable_Main}
				name={NamingConvention.lower_case_dash.convert(this.props.entity.name, NamingConvention.Pascal_Case_Space)}
				checkbox={true}
				columns={getColumns()}
				getRows={async (page, size, sorts) =>
				{
					let res: { rows: RowType[], count: number } = { rows: [], count: 0 };
					if (this.NSTable_Main.current)
					{
						let items = this.NSFilterBox_Top.current?.getFilterItems() ?? [];
						if (this.props.entity.server.list)
							res = await this.props.entity.server.list(items, page, size, sorts);
					}
					this.setState({ res });
					return res;
				}}
				getRowKey={getRowKey}
				getRowAttributes={(row_value: RowType, row_index: number) => { return this.props.ui?.getRowAttributes?.(row_value, row_index, this.state.res) ?? {}; }}
				getColumnAttributes={this.props.ui?.getColumnAttributes}
				getCellFormattedValue={this.props.ui?.getCellFormattedValue}
				onSelectedIDsChanged={() =>
				{
					if (this.props.onIDsChanged)
						this.props.onIDsChanged();
					else
						this.setState({});
				}}
			/>;
		};
		let modes = this.props.ui?.modes ?? {};
		if (!modes)
			modes = {};
		if (!modes["Table"])
			modes = { Table: table_renderer, ...modes };

		let mode_names = Object.keys(modes);
		let mode_current = this.state.mode;
		if (!mode_names.includes(mode_current))
			mode_current = mode_names[0];

		return (
			<>
				<div
					id={this.props.id}
					className={`${this.props.classList?.join(" ") ?? ""}`}
					style={{ ...this.props.style }}
				>
					<NSRow
						style={{ justifyContent: "start" }}
					>
						<NSFilterBox
							ref={this.NSFilterBox_Top}
							filterItems={incommingFilterItems}
							getTables={() =>
							{
								return this.props.entity.tables.map(table =>
								{
									let columns = table.table.getColumns().map(c => { return { id: c.name, ...c, type: typeConvertor(c.type) }; });
									columns = columns.filter(c =>
									{
										if (this.props.ui?.search_box?.exclude?.show?.column)
											if (this.props.ui?.search_box?.exclude.show?.column(table.table.name, c.name))
												return false;
										return true;
									});
									return { name: table.table.name, text: table.table.text, columns, required: table.required };
								}).filter(t =>
								{
									if (this.props.ui?.search_box?.exclude?.show?.table)
										if (this.props.ui?.search_box?.exclude.show.table(t.name))
											return false;
									return true;
								});
							}}
							getMoreTables={async () =>
							{
								let ans: NSFilterBoxProps_Table[] = [];
								if (this.props.ctf.enabled)
									try
									{
										if (this.props.ctf.fields)
											if (this.props.ctf.fields.length > 0)
											{
												let name = this.props.entity.name + "_field";
												ans.push({
													name,
													text: "Fields",
													columns: [{
														id: "field_id",
														name: "field_id",
														text: "ID",
														type: FilterItemColumnType.Unknown,
														getValues: async (search) =>
														{
															return this.props.ctf.fields?.filter(c => SearchOperation.match(c.id, search)).map(c => { return { value: c.id, title: c.id } }) ?? [];
														}
													}, {
														id: "field_name",
														name: "field_id",
														text: "Name",
														type: FilterItemColumnType.Unknown,
														getValues: async (search) =>
														{
															return this.props.ctf.fields?.filter(c => SearchOperation.match(c.name, search)).map(c => { return { value: c.id, title: c.name } }) ?? [];
														}
													}, {
														id: "field_value",
														name: "value",
														text: "Value",
														type: FilterItemColumnType.Unknown
													},
													...this.props.ctf.fields.map(f =>
													{
														return {
															id: f.id,
															name: f.id,
															text: f.name,
															type: typeConvertor(f.type.type.type),
															getValues: async (search: string) =>
															{
																let values = await this.props.entity.server.value_list(name, "value", search, f.id, null);
																return values.map(v => { return { value: v, title: v } });
															}
														};
													}),],
													required: false
												});
											}

										if (this.props.ctf.categories)
											if (this.props.ctf.categories.length > 0)
												ans.push({
													name: this.props.entity.name + "_category",
													text: "Categories",
													columns: [{
														id: "category_id",
														name: "category_id",
														text: "ID",
														type: FilterItemColumnType.Unknown,
														getValues: async (search) =>
														{
															return this.props.ctf.categories?.filter(c => SearchOperation.match(c.id, search)).map(c => { return { value: c.id, title: c.id } }) ?? [];
														}
													}, {
														id: "category_name",
														name: "category_id",
														text: "Name",
														type: FilterItemColumnType.Unknown,
														getValues: async (search) =>
														{
															return this.props.ctf.categories?.filter(c => SearchOperation.match(c.name, search)).map(c => { return { value: c.id, title: c.name } }) ?? [];
														}
													}],
													required: false
												});

									} catch (error)
									{
									}
								// todo add tags
								return ans;
							}}
							getValues={async (table: NSFilterBoxProps_Table, column: NSFilterBoxProps_Column, value: string) =>
							{
								if (this.props.ui?.search_box?.exclude?.load_value)
									if (this.props.ui?.search_box?.exclude?.load_value(table.name, column.name))
										return [];
								let values = await this.props.entity.server.value_list(table.name, column.name, value, null, null);
								return values.map(v => { return { value: v, title: v } });
							}}
							onChanged={() =>
							{
								this.NSTable_Main.current?.setSelectedIDs([], []);
								this.NSTable_Main.current?.reload(null, null);
							}} />
						<div className={Styles.nsa_section_list_items}>
							{
								mode_names.length > 1 &&
								<NSBoxCombo
									ref={this.NSBoxCombo_Mode}
									title="Mode"
									hideHeader
									required
									searchable={false}
									placeholder=""
									defaultValue={mode_current}
									getOptions={() =>
									{
										return mode_names.map(m => { return { value: m, title: m } });
									}}
									multiple={false}
									style={{ width: "100px" }}
									onChanged={e =>
									{
										this.setState({ mode: e.getValueOne(false) ?? "" });
									}}
								/>
							}
							<NSButton
								title=""
								onClick={{
									action: () =>
									{
										this.setState({ show_dialog_columns: true, sorts: this.NSTable_Main.current?.getSortItems() ?? [] }, () =>
										{
											let values = this.NSTable_Main.current?.getVisibleColumns().map(c => c.table + "." + c.column);
											if (values)
												this.NSBoxBooleans_Columns.current?.setValues(values);
										});
									},
									showLoading: false
								}}
								style={{
									width: "48px",
									border: "1px solid black",
									backgroundImage: "url('https://static.namirasoft.com/image/concept/gear/grey.svg')",
									backgroundSize: "32px",
									backgroundRepeat: "no-repeat",
									backgroundPosition: "center center"
								}}
							></NSButton>
						</div>
					</NSRow>
					<NSSpace size={NSSpaceSizeType.MINI} />
					<NSFilterBoxItems
						filterItems={filterItems}
						onClearFilterItems={() =>
						{
							this.NSFilterBox_Top.current?.setFilterItems([]);
						}}
						onRemoveFilterItem={(item) =>
						{
							this.NSFilterBox_Top.current?.removeFilterItem(item);
						}}
					/>
					{
						filterItems.length > 0 &&
						<NSSpace size={NSSpaceSizeType.MINI} />
					}
					{
						modes[mode_current](this.props.entity)
					}
					{
						id &&
						<>
							<NSSpace size={NSSpaceSizeType.SMALL} />
							<NSLine />
							<NSSpace size={NSSpaceSizeType.SMALL} />
							<NSASectionViewTabPage<RowType, RowTypeInput>
								ref={this.NSASectionViewTabPage_Ref}
								{...this.props}
								ui={this.props.ui?.tab_page_view}
								model={{
									id,
									row: null
								}}
							/>
						</>
					}
				</div >
				{
					this.state.show_dialog_columns &&
					<NSDialog
						onClose={() =>
						{
							try
							{
								if (this.NSBoxBoolean_Sort?.current?.getValueBool())
								{
									let column = this.NSBoxBaseCombo_Sort_Column.current?.getDataOne();
									let d = this.NSBoxCombo_Sort_Direction.current?.getValueOne();
									if (column)
									{
										let t = new BaseMetaTable(null, column.table.name, column.table.text);
										let c = new BaseMetaColumn(t, column.name, column.text, "", true);
										let sort = new SortItem(t, c, d === "true");
										this.NSTable_Main.current?.setSortItems([sort]);
									}
								}
								else
									this.NSTable_Main.current?.setSortItems(null);

								let cs_all = getColumns();
								let cs_visibles = this.NSBoxBooleans_Columns.current?.getValues() ?? [];
								let cs = cs_visibles.map(v => cs_all.filter(c => v === c.table.name + "." + c.name)[0]).filter(c => c);
								this.NSTable_Main.current?.setVisibleColumns(cs.map(c => { return { table: c.table.name, column: c.name }; }));
								this.setState({ show_dialog_columns: false });
							} catch (error)
							{
								alert(error)
							}
						}}
						center_dialog
						closeButton={{
							show: true,
							out: true
						}}
					>
						<NSPanel>
							<NSColumn>
								<NSTitle title="Sort" style={{ color: "rgba(20, 27, 92, 1)", marginBottom: "0" }} />
							</NSColumn>
							<NSRow
								style={{ justifyContent: "start" }}
							>
								<NSBoxBoolean
									ref={this.NSBoxBoolean_Sort}
									required
									title="Sort"
									checked={this.state.sorts.length > 0}
									onChanged={() =>
									{
										this.setState({});
									}}
								/>
								{
									this.NSBoxBoolean_Sort?.current?.getValueBool(false) &&
									<>
										<NSBoxBaseCombo<TableColumnInfo>
											ref={this.NSBoxBaseCombo_Sort_Column}
											title="Column"
											required
											defaultValue={this.state.sorts[0]?.column.name}
											getItems={async () =>
											{
												let cs = getColumns();
												return { count: cs.length, rows: cs };
											}}
											getTitle={c => c.text}
											getValue={c => c.name}
											getSort={() => null}
											multiple={false}
										/>
										<NSBoxCombo
											ref={this.NSBoxCombo_Sort_Direction}
											title="Direction"
											required
											defaultValue={this.state.sorts[0]?.ascending + ""}
											getOptions={() =>
											{
												return [{
													value: "true",
													title: "Ascending"
												}, {
													value: "false",
													title: "Descending"
												}];
											}}
											multiple={false}
										/>
									</>
								}
							</NSRow>
							<NSColumn>
								<NSTitle title="Columns" style={{ color: "rgba(20, 27, 92, 1)", marginBottom: "0" }} />
							</NSColumn>
							<NSRow style={{ maxWidth: NSBox.width.triple }}>
								<NSBoxBooleans
									ref={this.NSBoxBooleans_Columns}
									box_all={{ hideHeader: true }}
									boxes={{ hideHeader: true }}
									items={
										getColumns().map(c => { return { value: c.table.name + "." + c.name, text: c.table.text + "." + c.text } })
									}
								/>
							</NSRow>
						</NSPanel>
					</NSDialog>
				}
			</>
		);
	}
}